import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { getCard, type DrawnCard } from '@tarot/shared';

/** 뽑은 카드 확대 보기 (라이트박스). 배경 클릭 / ESC / 닫기 버튼으로 닫힘 */
export function CardLightbox({
  drawn,
  open,
  onClose,
}: {
  drawn: DrawnCard;
  open: boolean;
  onClose: () => void;
}) {
  const card = getCard(drawn.cardId);
  const dir = drawn.reversed ? '역방향' : '정방향';
  const keywords = drawn.reversed ? card.keywordsRev : card.keywordsUp;

  // ESC 닫기 + 배경 스크롤 잠금
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={`${card.nameKo} ${dir} 확대 보기`}
          // tw-on-dark: 배경이 테마와 무관하게 검정이라, 안쪽 글자·테두리는 다크 팔레트로 고정한다
          // (라이트 테마의 어두운 잉크가 그대로 쓰이면 검은 배경에 묻혀 안 보인다)
          className="tw-on-dark fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-black/85 p-6 backdrop-blur-sm"
        >
          <motion.img
            initial={{ scale: 0.75, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            src={`/${card.imageKey}`}
            alt={`${card.nameKo} ${dir}`}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            className={`max-h-[65dvh] w-auto select-none rounded-xl border border-gold/50 shadow-2xl shadow-black/70 ${
              drawn.reversed ? 'rotate-180' : ''
            }`}
          />
          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            <p className="text-lg font-semibold">
              {card.nameKo}{' '}
              <span className="text-sm text-moon-dim">
                ({card.nameEn} · {dir})
              </span>
            </p>
            <p className="mt-1 text-sm text-gold">{keywords.join(' · ')}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full border border-white/20 px-5 py-2 text-sm text-moon-dim transition hover:border-gold/60 hover:text-moon"
          >
            닫기
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
